/* eslint-disable no-unused-vars */
'use client'

import { initializePaddle, Paddle } from '@paddle/paddle-js'
import { Dispatch, PropsWithChildren, createContext, useContext, useEffect, useReducer } from 'react'

import { getCustomerIdFromPaddle } from '@/services/subscription-service'

interface PaddleState {
  paddle: Paddle | null
  customerId: string | null
}

export enum PaddleActionType {
  INIT = 'init',
  SET_CUSTOMER_ID = 'set_customer_id',
}

interface PaddleAction {
  type: PaddleActionType
  payload?: {
    paddle?: Paddle | null
    customerId?: string | null
  }
}

const paddleInitialState: PaddleState = {
  paddle: null,
  customerId: null,
}

const PaddleContext = createContext<{
  PaddleState: PaddleState
  dispatch: Dispatch<PaddleAction>
}>({
  PaddleState: paddleInitialState,
  dispatch: () => {},
})

const PaddleReducer = (state: PaddleState, action: PaddleAction): PaddleState => {
  switch (action.type) {
    case PaddleActionType.INIT: {
      return {
        ...state,
        paddle: action.payload?.paddle || null,
      }
    }
    case PaddleActionType.SET_CUSTOMER_ID: {
      return {
        ...state,
        customerId: action.payload?.customerId || null,
      }
    }
    default: {
      return state
    }
  }
}

export const PaddleProvider = ({ children }: PropsWithChildren<any>) => {
  const [PaddleState, dispatch] = useReducer(PaddleReducer, paddleInitialState)

  useEffect(() => {
    const initPaddle = async () => {
      const authToken = process.env.NEXT_PUBLIC_PADDLE_AUTH_TOKEN
      const environment = process.env.NEXT_PUBLIC_ENVIRONMENT_NAME === 'production' ? 'production' : 'sandbox'

      try {
        const { data, ok } = await getCustomerIdFromPaddle()

        const paddleInstance = await initializePaddle({
          environment,
          token: String(authToken),
          pwCustomer: ok && data?.customerId ? { id: data.customerId } : {},
        })

        if (paddleInstance) {
          dispatch({ type: PaddleActionType.INIT, payload: { paddle: paddleInstance } })

          if (ok && data?.customerId) {
            dispatch({
              type: PaddleActionType.SET_CUSTOMER_ID,
              payload: { customerId: data.customerId },
            })
          }
        }
      } catch (error) {
        console.error('Error initializing Paddle:', error)
      }
    }

    initPaddle()
  }, [])

  return <PaddleContext.Provider value={{ PaddleState, dispatch }}>{children}</PaddleContext.Provider>
}

export const usePaddle = () => {
  const context = useContext(PaddleContext)
  if (!context) throw new Error('usePaddle must be used within a PaddleProvider')
  return context
}
